import React, { useState } from 'react'
import { Button } from 'primereact/button'

import CurrentSelect from '../../../ui/CurrentSelect'
import Text from '../../../ui/Text'
import useText from '../../hooks/useText'
import { Dialog } from '../../uikit/Dialog'
import { formatTimeRange } from '../../uikit/Scheduler'
import { ScheduleEntry } from '../../uikit/Scheduler/types'

export type ScheduledTimeSlotEditorDialogProps = {
    minPossibleCurrent?: number
    maxPossibleCurrent?: number
    visible: boolean
    onHide: () => void
    onSave: (schedulePeriod: ScheduleEntry) => void
    selectedSchedulePeriod: ScheduleEntry | null
    isSocket?: boolean
}

type CurrentDraft = {
    schedulePeriodId: string
    sourceCurrent: number
    value: number
}

export function ScheduledTimeSlotEditorDialog({
    minPossibleCurrent,
    maxPossibleCurrent,
    selectedSchedulePeriod,
    isSocket,
    visible,
    onHide,
    onSave,
}: ScheduledTimeSlotEditorDialogProps): React.ReactElement {
    const text = useText()
    const [currentDraft, setCurrentDraft] = useState<CurrentDraft | null>(null)

    const currentValue =
        currentDraft &&
        selectedSchedulePeriod &&
        currentDraft.schedulePeriodId === selectedSchedulePeriod.id &&
        currentDraft.sourceCurrent === selectedSchedulePeriod.maxCurrent
            ? currentDraft.value
            : selectedSchedulePeriod?.maxCurrent

    const timeRange = selectedSchedulePeriod
        ? formatTimeRange(selectedSchedulePeriod.from, selectedSchedulePeriod.to)
        : null

    const save = () => {
        if (selectedSchedulePeriod && typeof currentValue === 'number') {
            setCurrentDraft(null)
            onSave({ ...selectedSchedulePeriod, maxCurrent: currentValue })
        }
    }

    const hide = () => {
        setCurrentDraft(null)
        onHide()
    }

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button text label={text('btnCancel')} type="button" onClick={hide} data-testid="cancel-btn" />
            <Button label={text('btnApply')} type="button" onClick={save} data-testid="save-btn" />
        </div>
    )

    return (
        <Dialog
            header={text('lblEditScheduledTimeSlotDialogTitle')}
            visible={visible}
            onHide={hide}
            footer={footer}
            data-testid="scheduled-time-slot-editor-dialog"
        >
            <div className="flex flex-column gap-3">
                {timeRange ? (
                    <dl className="flex flex-column gap-2 m-0">
                        <dt className="p-label">{text('lblScheduledTimeSlotTimeRangeField')}</dt>
                        <dd className="m-0 font-semibold">{timeRange}</dd>
                    </dl>
                ) : null}
                {isSocket ? (
                    <div className="text-sm font-bold py-3">
                        <Text code="lblSocketChargingStatus" values={{ isOn: !!selectedSchedulePeriod?.maxCurrent }} />
                    </div>
                ) : (
                    <CurrentSelect
                        label={text('lblScheduledTimeSlotMaximumCurrentField')}
                        value={currentValue || 0}
                        minValue={minPossibleCurrent}
                        maxValue={maxPossibleCurrent}
                        showClear={false}
                        onChange={({ value }) => {
                            if (typeof value === 'number' && selectedSchedulePeriod) {
                                setCurrentDraft({
                                    schedulePeriodId: selectedSchedulePeriod.id,
                                    sourceCurrent: selectedSchedulePeriod.maxCurrent,
                                    value,
                                })
                            }
                        }}
                        placeholder={text('lblSelectOption')}
                        error={typeof currentValue !== 'number' ? text('msgMaxCurrentRequired') : undefined}
                    />
                )}
            </div>
        </Dialog>
    )
}
